import { FC, useEffect, useState } from "react";
import { useSnackbar } from "notistack";

import { Button, FormControl, MenuItem, Select, Stack, Switch, TextField } from "@mui/material";

import { api } from "../../../../../utils";
import { AdminUser } from "../ViewAdminUsers/ViewAdminUsers";
import { IError } from "../../../../../models/ReportPageModels";

interface AdminIccUsers {
    data: AdminUsersData;
    error: IError;
}
interface AdminUsersData {
    users: AdminUser[];
}

interface EditAdminUserRequest {
    id: number;
    username: string;
    alitSystems: number;
    iccGeneral: number;
    mobile: number;
    billing: number;
    xero: number;
    other: number;
}
interface EditAdminUserResponse {
    data: EditAdminUserData;
    error: IError;
}
interface EditAdminUserData {
    status: string;
}

interface EditAdminUserProps {
    editUser: AdminUser | null;
}

const EditAdminUser: FC<EditAdminUserProps> = ({ editUser }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [userId, setUserId] = useState<number>(0);
    const [username, setUsername] = useState<string>("");
    const [alitSystems, setAlitSystems] = useState<number>(0);
    const [iccGeneral, setIccGeneral] = useState<number>(0);
    const [mobile, setMobile] = useState<number>(0);
    const [billing, setBilling] = useState<number>(0);
    const [xero, setXero] = useState<number>(0);
    const [other, setOther] = useState<number>(0);

    const [users, setUsers] = useState<AdminUser[]>([]);

    useEffect(() => {
        if (users.length === 0) {
            GetAdminUsers().then((temp: AdminUser[] | undefined) => {
                if (editUser === null)
                    return;
                if (temp === undefined)
                    return;

                var user = null;

                for (var i = 0; i < temp.length; i++) {
                    if (temp[i].id === editUser.id) {
                        user = temp[i]
                        break;
                    }
                }

                if (user === null)
                    return;

                setUserId(user.id);
                setUsername(user.username);
                setAlitSystems(user.alitSystems);
                setIccGeneral(user.iccGeneral);
                setMobile(user.mobile);
                setBilling(user.billing);
                setXero(user.xero);
                setOther(user.other);
            });
        }
    }, [editUser])

    const GetAdminUsers = async () => {
        setUsers([]);
        try {
            const response: AdminIccUsers = await api.get("api/Admin/GetAdminUsers", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setUsers(response.data.users);
                return response.data.users;
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const EditClient = async () => {
        if (userId === 0 || username === "") {
            enqueueSnackbar("Please recheck fields", { variant: "error" });
            return;
        }

        var body: EditAdminUserRequest = {
            id: userId,
            username: username,
            alitSystems: alitSystems,
            iccGeneral: iccGeneral,
            mobile: mobile,
            billing: billing,
            xero: xero,
            other: other,
        }

        try {
            const response: EditAdminUserResponse = await api.post("api/Admin/EditAdminUser", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Edited!", { variant: "success" })
                // clear fields
                setUserId(0);
                setUsername("");
                setAlitSystems(0);
                setIccGeneral(0);
                setMobile(0);
                setBilling(0);
                setXero(0);
                setOther(0);

                // get new users from db
                GetAdminUsers();
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SetUserId(event: any) {
        var value: number = event.target.value;
        setUserId(value);
        var user = null;

        for (var i = 0; i < users.length; i++) {
            if (users[i].id === value) {
                user = users[i]
                break;
            }
        }

        if (user === null)
            return;

        setUserId(user.id);
        setUsername(user.username);
        setAlitSystems(user.alitSystems);
        setIccGeneral(user.iccGeneral);
        setMobile(user.mobile);
        setBilling(user.billing);
        setXero(user.xero);
        setOther(user.other);
    }

    return (
        <Stack>
            {/* Users */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Users: </p>
                <FormControl sx={{ minWidth: 245 }}>
                    <Select
                        value={userId}
                        onChange={SetUserId}
                        size="small"
                        defaultValue={-1}>
                        <MenuItem
                            value={0}>
                            {"Select user"}
                        </MenuItem>
                        {users.map((user, index) =>
                            <MenuItem
                                key={index}
                                value={user.id}>
                                {user.username}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* Username */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Username: </p>
                <TextField
                    value={username}
                    onChange={(newValue: any) => { setUsername(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>
            {/* ALIT systems */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>ALIT systems: </p>
                <Switch
                    checked={alitSystems == 1}
                    onChange={(newValue) => { setAlitSystems(newValue.target.checked ? 1 : 0) }}
                    size="small" />
            </Stack>
            {/* ICC General */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>ICC General: </p>
                <Switch
                    checked={iccGeneral == 1}
                    onChange={(newValue) => { setIccGeneral(newValue.target.checked ? 1 : 0) }}
                    size="small" />
            </Stack>
            {/* Mobile */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Mobile: </p>
                <Switch
                    checked={mobile == 1}
                    onChange={(newValue) => { setMobile(newValue.target.checked ? 1 : 0) }}
                    size="small" />
            </Stack>
            {/* Billing */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Billing: </p>
                <Switch
                    checked={billing == 1}
                    onChange={(newValue) => { setBilling(newValue.target.checked ? 1 : 0) }}
                    size="small" />
            </Stack>
            {/* Xero */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Xero: </p>
                <Switch
                    checked={xero == 1}
                    onChange={(newValue) => { setXero(newValue.target.checked ? 1 : 0) }}
                    size="small" />
            </Stack>
            {/* Other */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Other: </p>
                <Switch
                    checked={other == 1}
                    onChange={(newValue) => { setOther(newValue.target.checked ? 1 : 0) }}
                    size="small" />
            </Stack>

            <Button
                variant="outlined"
                onClick={() => EditClient()}
                sx={{ width: '82px' }}>
                Edit
            </Button>
        </Stack>
    )
}

export default EditAdminUser;